// arrowCursorGlyph — カーソル種別「矢印」の独自 SVG グリフ (single source of truth)。
//
// なぜ必要か (consult 確定済み設計 B′、2026-07-25):
//   カーソル種別「矢印」は canvas を `cursor: auto` (OS 標準カーソル) にしていたが、
//   Chromium は pen の pointer capture 中はネイティブカーソルを隠すため、**描画中に
//   矢印が消える** (styles.css 2026-05-24 コメント参照)。
//   対策 B′ = 「hover もストローク中も同一の独自矢印グリフ」にしてすり替わりの
//   違和感を消す:
//     - hover: この module が作る data URI を `cursor: url(...) hotspotX hotspotY, auto`
//       として CSS に渡す (ブラウザ描画、ゼロ遅延)。
//     - ストローク中: BrushCursorOverlay が同じ SVG を <img> で表示する
//       (ネイティブカーソルは innerWrapper の `data-oekaki-stroking` 属性で
//       `cursor: none` にして隠す、= 二重矢印防止)。
//   どちらの経路も **この 1 ファイルの SVG マークアップだけ**を参照するので、
//   二重定義 (= 見た目が食い違う事故) が構造的に起きない。
//
// 見た目: Windows クラシック風の白地・黒縁シンプル矢印 (Java 版の懐古フック)。
// リング (BrushCursorOverlay の brush-ring モード) と違い、**固定サイズ**
// (zoom / ブラシ幅でスケールしない) — 矢印は「位置を指す」役割であり、
// ブラシの実効範囲を示すリングとは意味が違うため。
//
// ホットスポット = 矢印の先端 (= このパスの始点と同じ座標)。CSS cursor の
// hotspot 引数と、overlay 側の位置合わせ (先端をポインタ位置に揃える translate)
// の両方がこの座標を参照する。

/** グリフの表示サイズ (画面 px、正方形の SVG viewBox と一致)。固定値。 */
export const ARROW_CURSOR_SIZE_PX = 20;

/** 矢印の先端 (= ホットスポット)。パスの始点と同一座標にすること。 */
export const ARROW_CURSOR_HOTSPOT = { x: 2, y: 2 } as const;

/**
 * Windows クラシック風の矢印 (白地・黒縁)。viewBox は正方形
 * `ARROW_CURSOR_SIZE_PX`、パス始点 = `ARROW_CURSOR_HOTSPOT` (矢印の先端)。
 * stroke-linejoin/linecap: round で角の描画差 (ブラウザ間のドット欠け) を抑える。
 */
export const ARROW_CURSOR_SVG_MARKUP =
  `<svg xmlns="http://www.w3.org/2000/svg" width="${ARROW_CURSOR_SIZE_PX}" height="${ARROW_CURSOR_SIZE_PX}" viewBox="0 0 ${ARROW_CURSOR_SIZE_PX} ${ARROW_CURSOR_SIZE_PX}">` +
  `<path d="M2,2 L2,16.5 L5.8,13 L8.7,19.6 L11.4,18.4 L8.5,11.9 L14.4,11.9 Z" ` +
  `fill="#ffffff" stroke="#000000" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"/>` +
  `</svg>`;

/**
 * SVG を data URI 化する。`encodeURIComponent` 方式 (= btoa 不使用): 本グリフの
 * マークアップは非 ASCII 文字を含まないため base64 化の必要が無く、ブラウザの
 * data URI 実装との相性 (改行・引用符の扱い) も安定している定石。
 */
export const ARROW_CURSOR_DATA_URI = `data:image/svg+xml,${encodeURIComponent(ARROW_CURSOR_SVG_MARKUP)}`;

/**
 * CSS `cursor` プロパティにそのまま渡せる値。hotspot 引数付き `url(...)` +
 * 非対応環境向け `auto` フォールバック。styles.css は静的にこの文字列を
 * 埋め込まず、CanvasStage が inline style の CSS Custom Property
 * (`--oekaki-arrow-cursor`) 経由でこの値を流し込む (= SVG 定義を CSS 側に
 * 複製しない、二重定義防止の設計判断)。
 */
export const ARROW_CURSOR_CSS_VALUE = `url("${ARROW_CURSOR_DATA_URI}") ${ARROW_CURSOR_HOTSPOT.x} ${ARROW_CURSOR_HOTSPOT.y}, auto`;
